/**
 * Storage resilience: request durable IndexedDB/Cache-Storage persistence and warn when
 * the device is at eviction risk.
 *
 * Offline-first field data (un-synced inspections in IndexedDB, photos in Cache Storage)
 * can be silently EVICTED by the browser under storage pressure unless the origin has been
 * granted persistent storage. This module asks for persistence once at startup and reports
 * usage so the app can warn a field user to sync + free space before data is lost.
 *
 * Pure + dependency-injected (the StorageManager and the notifier are passed in), so the
 * decision logic is unit-testable without a DOM, a running browser, or the toast store.
 */

export interface StorageStatus {
  /** The StorageManager API is available in this environment. */
  supported: boolean;
  /** The origin has been granted persistent (eviction-resistant) storage. */
  persisted: boolean;
  usageBytes: number | null;
  quotaBytes: number | null;
  /** usage / quota in [0, 1], or null when it can't be determined. */
  usageRatio: number | null;
}

export type StorageManagerLike = Pick<
  StorageManager,
  'persist' | 'persisted' | 'estimate'
>;

/** Warn a field user to sync + free space at/above this fraction of the quota. */
export const HIGH_USAGE_WARN_RATIO = 0.85;

function resolveManager(
  injected?: StorageManagerLike,
): StorageManagerLike | null {
  if (injected) return injected;
  if (typeof navigator !== 'undefined' && navigator.storage) {
    return navigator.storage;
  }
  return null;
}

/**
 * Ask the browser for persistent storage (idempotent — a no-op if already granted).
 * Returns whether the origin ends up persisted. Never throws.
 */
export async function requestPersistentStorage(
  injected?: StorageManagerLike,
): Promise<boolean> {
  const sm = resolveManager(injected);
  if (!sm?.persist || !sm.persisted) return false;
  try {
    if (await sm.persisted()) return true;
    return await sm.persist();
  } catch {
    return false;
  }
}

/** Read persistence + usage/quota. Never throws; unknown fields come back null. */
export async function getStorageStatus(
  injected?: StorageManagerLike,
): Promise<StorageStatus> {
  const sm = resolveManager(injected);
  if (!sm) {
    return {
      supported: false,
      persisted: false,
      usageBytes: null,
      quotaBytes: null,
      usageRatio: null,
    };
  }
  let persisted = false;
  let usageBytes: number | null = null;
  let quotaBytes: number | null = null;
  let usageRatio: number | null = null;
  try {
    persisted = sm.persisted ? await sm.persisted() : false;
  } catch {
    /* leave persisted false */
  }
  try {
    if (sm.estimate) {
      const estimate = await sm.estimate();
      usageBytes = estimate.usage ?? null;
      quotaBytes = estimate.quota ?? null;
      usageRatio =
        usageBytes != null && quotaBytes ? usageBytes / quotaBytes : null;
    }
  } catch {
    /* leave usage null */
  }
  return { supported: true, persisted, usageBytes, quotaBytes, usageRatio };
}

export interface StorageWarning {
  warn: boolean;
  reason?: 'high-usage' | 'not-persisted';
  message?: string;
}

/**
 * Pure decision: should the user be warned about eviction risk? High usage is the more
 * urgent signal (checked first); a denied persistence grant is a standing caution.
 */
export function evaluateStorageWarning(status: StorageStatus): StorageWarning {
  if (!status.supported) return { warn: false };
  if (status.usageRatio != null && status.usageRatio >= HIGH_USAGE_WARN_RATIO) {
    return {
      warn: true,
      reason: 'high-usage',
      message:
        'This device is low on storage. Sync your inspections and free up space to avoid losing un-synced work.',
    };
  }
  if (!status.persisted) {
    return {
      warn: true,
      reason: 'not-persisted',
      message:
        'The browser has not granted persistent storage, so un-synced inspections and photos could be cleared under storage pressure. Sync regularly.',
    };
  }
  return { warn: false };
}

/** Re-warn about a denied persistence grant at most once per period (it fires on EVERY
 * launch in environments where the browser denies persist(), e.g. non-engaged origins —
 * a scary toast on every start is noise, not signal). High-usage stays unthrottled: it is
 * urgent and actionable every time. */
export const NOT_PERSISTED_REWARN_MS = 24 * 60 * 60 * 1000;
const NOT_PERSISTED_STAMP_KEY = 'bdk_storage_notpersisted_warned_at';

interface KeyValueLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const browserKv = (): KeyValueLike | undefined => {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : undefined;
  } catch {
    return undefined;
  }
};

export interface InitStorageResilienceOptions {
  manager?: StorageManagerLike;
  /** Throttle bookkeeping store; defaults to localStorage. */
  kv?: KeyValueLike;
  now?: () => number;
}

/**
 * Startup entry point: request persistence, read status, and notify the user if at risk.
 * `notify` is injected so this module never imports the toast store (keeps it unit-testable).
 */
export async function initStorageResilience(
  notify: (message: string) => void,
  injected?: StorageManagerLike,
  options: Omit<InitStorageResilienceOptions, 'manager'> = {},
): Promise<StorageStatus> {
  const kv = options.kv ?? browserKv();
  const now = options.now ?? (() => Date.now());

  await requestPersistentStorage(injected);
  const status = await getStorageStatus(injected);
  const warning = evaluateStorageWarning(status);
  if (warning.warn && warning.message) {
    if (warning.reason === 'not-persisted') {
      // An absent stamp means "never warned" - only a real, recent timestamp suppresses.
      const raw = kv?.getItem(NOT_PERSISTED_STAMP_KEY);
      const last = raw == null ? null : Number(raw);
      if (
        last != null &&
        Number.isFinite(last) &&
        now() - last < NOT_PERSISTED_REWARN_MS
      ) {
        return status; // warned recently - stay quiet
      }
      try {
        kv?.setItem(NOT_PERSISTED_STAMP_KEY, String(now()));
      } catch {
        /* throttle bookkeeping is best-effort */
      }
    }
    notify(warning.message);
  }
  return status;
}
